import React, { useState, useEffect } from 'react';
import bookingService from '../../../../../../services/booking.service';
import { useEyeconxStore } from '../../../../../../store/eyeconxStore';
import { confirmDelete, showError, showSuccess } from '../../../../../../utils/ecxSwal';
import { ClinicInfoPanel } from '../../info/clinicInfoParts';
import ClinicSettingsEmpty from '../../subparts/ClinicSettingsEmpty';
import ClinicSettingsCancelButton from '../../subparts/ClinicSettingsCancelButton';
import ClinicSettingsListSkeleton from '../../subparts/ClinicSettingsListSkeleton';

const DoctorAppointmentTypes = ({ doctor }) => {
    const clinicId = useEyeconxStore((state) => state.user.clinicId);
    const [allAppointmentTypes, setAllAppointmentTypes] = useState([]);
    const [doctorAppointmentTypes, setDoctorAppointmentTypes] = useState([]);
    const [selectedTypesToAdd, setSelectedTypesToAdd] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (doctor?.id) {
            loadData();
        }
    }, [doctor?.id, clinicId]);

    const loadData = async () => {
        setIsLoading(true);
        try {
            const [allRes, doctorRes] = await Promise.all([
                bookingService.getAllAppointmentTypes(clinicId),
                bookingService.getDoctorAppointmentTypes(clinicId, doctor.id),
            ]);
            setAllAppointmentTypes(allRes.data || []);
            setDoctorAppointmentTypes(doctorRes.data || []);
        } catch (error) {
            console.log('Error loading appointment types:', error);
            showError({
                title: 'Load Failed',
                text: 'Unable to load appointment types. Please try again.',
            });
        } finally {
            setIsLoading(false);
        }
    };

    const deleteAppointmentType = async (type) => {
        const result = await confirmDelete({
            title: 'Remove Appointment Type?',
            itemName: type.apptType,
            description: 'This appointment type will be removed from the doctor profile.',
            onConfirm: () =>
                bookingService.deleteDoctorAppointmentTypes(clinicId, doctor.id, [type.appointmentTypeId]),
        });

        if (result.isConfirmed) {
            await loadData();
        }
    };

    const addSelectedAppointmentTypes = async () => {
        if (selectedTypesToAdd.length === 0) return;

        setIsSaving(true);
        try {
            await bookingService.addUpdateDoctorAppointmentTypes(clinicId, doctor.id, selectedTypesToAdd);
            setSelectedTypesToAdd([]);
            await loadData();
            await showSuccess({
                title: 'Types Added',
                text: 'Appointment types were assigned successfully.',
            });
        } catch (error) {
            console.log('Error adding appointment types:', error);
            showError({
                title: 'Add Failed',
                text: 'Unable to assign appointment types. Please try again.',
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleCheckboxChange = (appointmentTypeId) => {
        setSelectedTypesToAdd((prev) =>
            prev.includes(appointmentTypeId)
                ? prev.filter((id) => id !== appointmentTypeId)
                : [...prev, appointmentTypeId]
        );
    };

    const handleSelectAllAvailable = () => {
        const availableTypes = getAvailableAppointmentTypes();
        if (selectedTypesToAdd.length === availableTypes.length) {
            setSelectedTypesToAdd([]);
        } else {
            setSelectedTypesToAdd(availableTypes.map((type) => type.id));
        }
    };

    const handleCancel = () => {
        setSelectedTypesToAdd([]);
        setIsEditing(false);
    };

    const getAvailableAppointmentTypes = () => {
        const doctorTypeIds = doctorAppointmentTypes.map((type) => type.appointmentTypeId);
        return allAppointmentTypes.filter((type) => !doctorTypeIds.includes(type.id));
    };

    const enrichDoctorType = (type) => {
        const clinicType = allAppointmentTypes.find((item) => item.id === type.appointmentTypeId);
        return {
            ...type,
            isActive: type.isActive ?? clinicType?.isActive ?? false,
        };
    };

    const enrichedDoctorTypes = doctorAppointmentTypes.map(enrichDoctorType);

    const panelActions = !isEditing ? (
        <button
            type="button"
            className="btn btn-sm btn-light-primary rounded-pill"
            onClick={() => setIsEditing(true)}
        >
            <i className="bi bi-pencil me-1"></i>
            Manage
        </button>
    ) : (
        <ClinicSettingsCancelButton onClick={handleCancel} disabled={isSaving} />
    );

    if (isLoading) {
        return (
            <ClinicInfoPanel title="Appointment Types" icon="bi-calendar-check">
                <ClinicSettingsListSkeleton rows={3} />
            </ClinicInfoPanel>
        );
    }

    const availableAppointmentTypes = getAvailableAppointmentTypes();

    return (
        <ClinicInfoPanel title="Appointment Types" icon="bi-calendar-check" actions={panelActions}>
            {!isEditing ? (
                enrichedDoctorTypes.length === 0 ? (
                    <ClinicSettingsEmpty
                        icon="bi-calendar-x"
                        title="No appointment types assigned"
                        description="Assign appointment types so patients can book with this doctor."
                        actionLabel="Manage Types"
                        onAction={() => setIsEditing(true)}
                    />
                ) : (
                    <div className="doctor-appt-types">
                        {enrichedDoctorTypes.map((type) => (
                            <div key={type.id} className="doctor-appt-type-chip">
                                <span className="doctor-appt-type-chip__name">{type.apptType}</span>
                                <span className="doctor-appt-type-chip__meta">{type.timeSlot} min</span>
                                <span className={`clinic-settings-status-pill ${type.isActive ? 'is-open' : 'is-closed'}`}>
                                    {type.isActive ? 'Active' : 'Inactive'}
                                </span>
                            </div>
                        ))}
                    </div>
                )
            ) : (
                <div className="doctor-appt-types-edit">
                    <div className="doctor-appt-types-edit__section">
                        <div className="doctor-appt-types-edit__header">
                            <h6 className="mb-0">Assigned Types</h6>
                            <span className="text-muted small">{enrichedDoctorTypes.length} assigned</span>
                        </div>
                        {enrichedDoctorTypes.length === 0 ? (
                            <ClinicSettingsEmpty
                                icon="bi-calendar-x"
                                title="No types assigned"
                                description="Select from available types below to assign."
                            />
                        ) : (
                            <div className="clinic-settings-record-list">
                                {enrichedDoctorTypes.map((type) => (
                                    <div key={type.id} className="clinic-settings-record-item">
                                        <span className="clinic-settings-record-item__avatar">
                                            <i className="bi bi-calendar-event"></i>
                                        </span>
                                        <div className="clinic-settings-record-item__main">
                                            <h6 className="clinic-settings-record-item__title">{type.apptType}</h6>
                                            <p className="clinic-settings-record-item__meta mb-0">{type.timeSlot} minutes</p>
                                        </div>
                                        <span className={`clinic-settings-status-pill ${type.isActive ? 'is-open' : 'is-closed'}`}>
                                            {type.isActive ? 'Active' : 'Inactive'}
                                        </span>
                                        <div className="clinic-settings-record-item__actions">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-danger rounded-circle"
                                                onClick={() => deleteAppointmentType(type)}
                                                title="Remove"
                                            >
                                                <i className="bi bi-trash"></i>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="doctor-appt-types-edit__section">
                        <div className="doctor-appt-types-edit__header">
                            <h6 className="mb-0">Available Types</h6>
                            {availableAppointmentTypes.length > 0 && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary rounded-pill"
                                    onClick={handleSelectAllAvailable}
                                >
                                    {selectedTypesToAdd.length === availableAppointmentTypes.length
                                        ? 'Deselect All'
                                        : 'Select All'}
                                </button>
                            )}
                        </div>
                        {availableAppointmentTypes.length === 0 ? (
                            <p className="text-muted small mb-0">All appointment types are already assigned.</p>
                        ) : (
                            <div className="doctor-appt-types-available">
                                {availableAppointmentTypes.map((type) => (
                                    <label key={type.id} className="doctor-appt-types-available__item">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={selectedTypesToAdd.includes(type.id)}
                                            onChange={() => handleCheckboxChange(type.id)}
                                        />
                                        <span className="doctor-appt-types-available__content">
                                            <strong>{type.apptType}</strong>
                                            <span className="text-muted small">{type.timeSlot} min</span>
                                        </span>
                                    </label>
                                ))}
                            </div>
                        )}
                        {selectedTypesToAdd.length > 0 && (
                            <button
                                type="button"
                                className="btn btn-sm btn-light-primary rounded-pill mt-3"
                                onClick={addSelectedAppointmentTypes}
                                disabled={isSaving}
                            >
                                <i className={`bi ${isSaving ? 'bi-arrow-clockwise ecx-refresh-spin' : 'bi-plus-circle'} me-1`}></i>
                                {isSaving ? 'Adding...' : `Add Selected (${selectedTypesToAdd.length})`}
                            </button>
                        )}
                    </div>
                </div>
            )}
        </ClinicInfoPanel>
    );
};

export default DoctorAppointmentTypes;
